/**
 * `transformCommands` (W1-10b core-commands-motion; DESIGN.md §5.4; rpe-editor-features.md E§3.8 EVT-09/10).
 *
 *  - `transform`: time offset and time scale p/q around a pivot (default: the selection's earliest beat) over notes
 *    and keys together, X offset / scale around a pivot over notes. Exact rational arithmetic (rounding counted in
 *    `tx.rounding`). Keys may not pass unselected keys and nothing may move before beat 0.
 *  - `insertTime` / `removeTime`: over the whole chart ('all': notes, keys, BPM points, markers, effects, videos,
 *    meter marks, variable tracks) or the given lines (their notes and motion). Inserting moves everything at or
 *    after `at` later; spanning segments, holds and effects are stretched. Removing deletes notes, markers, BPM
 *    points (never the beat-0 point) and effects inside [from, to), shrinks or cuts tracks (motionTime.ts) and moves
 *    later items earlier; the tempo in effect at `to` continues at `from`. Insert then remove of the same range is
 *    the identity. A curve spanning the whole range without a key inside is shortened as a whole (the inverse of
 *    the stretch), which also changes its part before `from`: noted with `cmdMotion.timeShrunk` (CR-W1-10b-1). On X/Y-linked lines both position axes of a layer are cut when either one is (keys at the same
 *    beats); approximate edge splits of a cut are noted. Imported lead-ins (the first segment continued backwards
 *    before a first key after beat 0): a cut whose range holds the first key keeps the lead-in as its left side
 *    (made explicit from beat 0); inserting or shrinking time before or across the first segment moves that
 *    segment, and with it the lead-in, which is measured and noted (`cmdMotion.timeLeadIn`, CR-W1-10b-12).
 *  - `reverseTime`: mirrors notes and the curves of the selected segments' tracks within the selection's span
 *    (easings reversed exactly; span edges inside a segment and tempo changes inside the span are split
 *    shape-preservingly first, approximations noted; an imported lead-in extrapolated before the span is made an
 *    explicit segment from beat 0 first, so it keeps its curve; X/Y-linked axes keep sharing their beats).
 *  - `negateKeys` (A): position, rotation and speed values negated; opacity keys to 0. `opacityFull` (S): opacity
 *    keys to 255.
 */

import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { BpmId, EffectId, KeyId, LineId, LineProp, MeterMark, Note, TrackId, TrackValue } from '../model/types';
import type { RecordPatch, TxBuilder } from '../ops/types';
import { valueEquals } from '../ops/equal';
import type { TransformCommands } from './types';
import {
  alignXyWindows,
  cleanLinks,
  linkedNeighbours,
  noteXyWindow,
  syncLinkGroups,
  withXyPartners,
  xyPartnerTrack,
  type XyWindow,
} from './link';
import { collectSplit, notePartnerSplit, noteWorstSplit, worseSplitOn, type SplitOn } from './motionNotes';
import {
  commitKeys,
  groupByTrack,
  MOTION_MSG,
  readKeys,
  reason,
  requireTrack,
  sameValue,
  tempoChangeBeats,
  tidy,
  type KeyRec,
  type TrackInfo,
} from './motionTrack';
import {
  captureLeadIn,
  cutJoinsSides,
  insertTimeKeys,
  leadInShiftReport,
  removeTimeKeys,
  reverseWindow,
  type LeadInBefore,
} from './motionTime';
import { curveCtx, setBoth } from './splitKey';
import type { TempoMap } from '../time/types';

/** Properties whose keys `A` negates (EVT-09). Opacity goes to 0 instead (EVT-10). */
export const NEGATE_PROPS: ReadonlySet<LineProp> = new Set<LineProp>(['moveX', 'moveY', 'rotate', 'speed']);

// ---------------------------------------------------------------------------------------------------------------
// transform
// ---------------------------------------------------------------------------------------------------------------

function sameOrder(before: readonly (KeyId | null)[], after: readonly KeyRec[]): boolean {
  return after.every((k, i) => k.id === before[i]);
}

const transform: TransformCommands['transform'] = (tx, { notes, keys, spec }) => {
  const [p, q] = spec.timeScale ?? [1, 1];
  if (!Number.isSafeInteger(p) || !Number.isSafeInteger(q) || p <= 0 || q <= 0)
    tx.fail(reason(MOTION_MSG.timeScaleInvalid));
  const dt = spec.dt ?? Beats.ZERO;
  const dx = spec.dx ?? 0;
  const xScale = spec.xScale ?? 1;
  const xPivot = spec.xPivot ?? 0;
  if (![dx, xScale, xPivot].every((v) => Number.isFinite(v))) tx.fail(reason(MOTION_MSG.valueNotFinite));
  const noteRecs = [...new Set(notes)].map((id) => tx.doc.notes.get(id)).filter((n): n is Note => n !== undefined);
  const keyIds = withXyPartners(
    tx,
    [...new Set(keys)].filter((id) => tx.doc.keys.has(id)),
  );
  if (noteRecs.length === 0 && keyIds.length === 0) tx.fail(reason(MOTION_MSG.transformNothing));
  let pivot = spec.timePivot ?? null;
  if (pivot === null) {
    for (const n of noteRecs) if (pivot === null || Beats.lt(n.t, pivot)) pivot = n.t;
    for (const id of keyIds) {
      const k = tx.doc.keys.get(id);
      if (k !== undefined && (pivot === null || Beats.lt(k.t, pivot))) pivot = k.t;
    }
  }
  const piv = pivot ?? Beats.ZERO;
  const scaled = p !== q;
  const mapT = (t: Beat): Beat => {
    const rel = scaled
      ? Beats.mulRat(Beats.sub(t, piv, tx.rounding), p, q, tx.rounding)
      : Beats.sub(t, piv, tx.rounding);
    return Beats.add(Beats.add(piv, rel, tx.rounding), dt, tx.rounding);
  };
  let focus: Beat | null = null;
  const lines = new Set<LineId>();
  for (const n of noteRecs) {
    const t = mapT(n.t);
    const end = n.kind === 'hold' ? mapT(n.end) : t;
    if (Beats.cmp(t, Beats.ZERO) < 0 && Beats.cmp(n.t, Beats.ZERO) >= 0) tx.fail(reason(MOTION_MSG.beforeZero));
    const x = tidy(xPivot + (n.x - xPivot) * xScale + dx);
    const patch: { -readonly [K in keyof Note]?: Note[K] } = {};
    if (!Beats.eq(t, n.t)) patch.t = t;
    if (!Beats.eq(end, n.end)) patch.end = end;
    if (!Object.is(x, n.x)) patch.x = x;
    if (Object.keys(patch).length > 0) tx.update('notes', n.id, patch);
    if (focus === null || Beats.lt(t, focus)) focus = t;
    lines.add(n.line);
  }
  for (const [trackId, ids] of groupByTrack(tx, keyIds)) {
    const info = requireTrack(tx, trackId);
    const recs = readKeys(tx, trackId);
    const order = recs.map((k) => k.id);
    const sel = new Set(ids);
    for (const k of recs) {
      if (k.id === null || !sel.has(k.id)) continue;
      const t = mapT(k.t);
      if (Beats.cmp(t, Beats.ZERO) < 0 && Beats.cmp(k.t, Beats.ZERO) >= 0) tx.fail(reason(MOTION_MSG.beforeZero));
      k.t = t;
      if (focus === null || Beats.lt(t, focus)) focus = t;
    }
    const sorted = [...recs].sort((a, b) => Beats.cmp(a.t, b.t));
    const strictly = sorted.every((k, i) => i === 0 || Beats.lt((sorted[i - 1] as KeyRec).t, k.t));
    if (!strictly || !sameOrder(order, sorted)) tx.fail(reason(MOTION_MSG.transformCollide));
    commitKeys(tx, info, sorted);
    if (info.line !== null) lines.add(info.line.id);
  }
  const line = lines.size === 1 ? [...lines][0] : undefined;
  if (focus !== null) tx.setFocus(line === undefined ? { beat: focus } : { beat: focus, line });
};

// ---------------------------------------------------------------------------------------------------------------
// Insert / remove time
// ---------------------------------------------------------------------------------------------------------------

function scopeLines(tx: TxBuilder, scope: 'all' | readonly LineId[]): LineId[] {
  if (scope === 'all') return [...tx.doc.lineOrder];
  return [...new Set(scope)].filter((id) => tx.doc.lines.has(id));
}

/** Tracks affected by a time operation: the lines' tracks, plus every variable track for the whole chart. */
function scopeTracks(tx: TxBuilder, lines: readonly LineId[], all: boolean): TrackId[] {
  const out: TrackId[] = [];
  for (const line of lines) for (const ids of tx.idx.tracksOfLine(line).values()) out.push(...ids);
  if (all) for (const t of tx.doc.tracks.values()) if (t.owner.kind !== 'line') out.push(t.id);
  return out;
}

function notePatch(n: Note, t: Beat, end: Beat): RecordPatch<Note> | null {
  const patch: { -readonly [K in keyof Note]?: Note[K] } = {};
  if (!Beats.eq(t, n.t)) patch.t = t;
  if (!Beats.eq(end, n.end)) patch.end = end;
  return Object.keys(patch).length > 0 ? patch : null;
}

/** A track whose extrapolated lead-in a time operation moved: measured once the tempo map is final. */
interface MovedLeadIn {
  readonly info: TrackInfo;
  readonly before: LeadInBefore;
  readonly after: readonly KeyRec[];
}

/**
 * Measures the lead-ins a time operation moved (motionTime.ts `leadInShiftReport`): the old curve with the tempo
 * map before the operation against the new one with the tempo map now (BPM points may have moved too).
 */
function movedLeadIns(tx: TxBuilder, moved: readonly MovedLeadIn[], oldTempo: TempoMap): SplitOn | null {
  let acc: SplitOn | null = null;
  for (const m of moved) {
    const now = curveCtx(tx, m.info);
    acc = collectSplit(acc, m.info, leadInShiftReport(m.before, m.after, { ...now, tempo: oldTempo }, now));
  }
  return acc;
}

/** BPM points sorted by beat, latest first (so shifted points never pass unshifted ones). */
function bpmLatestFirst(tx: TxBuilder): BpmId[] {
  return [...tx.idx.bpmSorted()].reverse();
}

const insertTime: TransformCommands['insertTime'] = (tx, { at, length, scope }) => {
  if (!Beats.lt(Beats.ZERO, length)) tx.fail(reason(MOTION_MSG.timeLengthInvalid));
  if (Beats.cmp(at, Beats.ZERO) < 0) tx.fail(reason(MOTION_MSG.timeRangeInvalid));
  const all = scope === 'all';
  const lines = scopeLines(tx, scope);
  const shift = (b: Beat): Beat => Beats.add(b, length, tx.rounding);
  const oldTempo = tx.tempo;
  const leadIns: MovedLeadIn[] = [];
  let moved = 0;
  for (const line of lines) {
    for (const id of [...tx.idx.notesByLine(line)].reverse()) {
      const n = tx.doc.notes.get(id);
      if (n === undefined) continue;
      const t = Beats.lte(at, n.t) ? shift(n.t) : n.t;
      const end = n.kind === 'hold' ? (Beats.lte(at, n.end) ? shift(n.end) : n.end) : t;
      const patch = notePatch(n, t, end);
      if (patch !== null) {
        tx.update('notes', n.id, patch);
        moved++;
      }
    }
  }
  for (const trackId of scopeTracks(tx, lines, all)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const lead = captureLeadIn(keys, curveCtx(tx, info), at);
    const count = insertTimeKeys(keys, at, length);
    if (count > 0) {
      commitKeys(tx, info, keys);
      moved += count;
      if (lead !== null) leadIns.push({ info, before: lead, after: keys });
    }
  }
  if (all) {
    for (const id of bpmLatestFirst(tx)) {
      const b = tx.doc.bpm.get(id);
      if (b === undefined || Beats.eq(b.t, Beats.ZERO) || Beats.lt(b.t, at)) continue;
      tx.update('bpm', id, { t: shift(b.t) });
      moved++;
    }
    for (const m of [...tx.doc.markers.values()]) {
      if (Beats.lte(at, m.t)) {
        tx.update('markers', m.id, { t: shift(m.t) });
        moved++;
      }
    }
    for (const e of [...tx.doc.effects.values()]) {
      const start = Beats.lte(at, e.start) ? shift(e.start) : e.start;
      const end = Beats.lte(at, e.end) ? shift(e.end) : e.end;
      if (!Beats.eq(start, e.start) || !Beats.eq(end, e.end)) {
        tx.update('effects', e.id, { start, end });
        moved++;
      }
    }
    for (const v of [...tx.doc.videos.values()]) {
      if (Beats.lte(at, v.time)) {
        tx.update('videos', v.id, { time: shift(v.time) });
        moved++;
      }
    }
    const meter = tx.doc.timing.meter.map((m): MeterMark =>
      Beats.lt(Beats.ZERO, m.t) && Beats.lte(at, m.t) ? { ...m, t: shift(m.t) } : m,
    );
    if (!valueEquals(meter, tx.doc.timing.meter)) tx.updateSingleton('timing', { meter });
  }
  noteWorstSplit(tx, movedLeadIns(tx, leadIns, oldTempo));
  tx.setFocus({ beat: at });
  return { moved };
};

/** Deletes an effect with its variable tracks. */
function deleteEffect(tx: TxBuilder, id: EffectId): void {
  const e = tx.doc.effects.get(id);
  if (e === undefined) return;
  for (const v of e.vars) {
    if (v.value.kind !== 'track') continue;
    const track = v.value.track;
    for (const k of [...tx.idx.keysByTrack(track)]) tx.remove('keys', k);
    tx.remove('tracks', track);
  }
  tx.setOrder(
    'effectOrder',
    tx.doc.effectOrder.filter((x) => x !== id),
  );
  tx.remove('effects', id);
}

const removeTime: TransformCommands['removeTime'] = (tx, { from, to, scope }) => {
  if (!Beats.lt(from, to) || Beats.cmp(from, Beats.ZERO) < 0) tx.fail(reason(MOTION_MSG.timeRangeInvalid));
  const all = scope === 'all';
  const lines = scopeLines(tx, scope);
  const length = Beats.sub(to, from);
  const back = (b: Beat): Beat => Beats.sub(b, length, tx.rounding);
  const inside = (b: Beat): boolean => Beats.lte(from, b) && Beats.lt(b, to);
  let removed = 0;
  let moved = 0;
  for (const line of lines) {
    for (const id of [...tx.idx.notesByLine(line)]) {
      const n = tx.doc.notes.get(id);
      if (n === undefined) continue;
      if (inside(n.t)) {
        tx.remove('notes', n.id);
        removed++;
        continue;
      }
      const t = Beats.lte(to, n.t) ? back(n.t) : n.t;
      let end = t;
      if (n.kind === 'hold') end = Beats.lte(to, n.end) ? back(n.end) : Beats.lt(from, n.end) ? from : n.end;
      const patch = notePatch(n, t, end);
      if (patch !== null) {
        tx.update('notes', n.id, patch);
        moved++;
      }
    }
  }
  if (all) {
    // Effects first: deleting one deletes its variable tracks.
    for (const e of [...tx.doc.effects.values()]) {
      const start = Beats.lte(to, e.start) ? back(e.start) : inside(e.start) ? from : e.start;
      const end = Beats.lte(to, e.end) ? back(e.end) : Beats.lt(from, e.end) ? from : e.end;
      if (!Beats.lt(start, end)) {
        deleteEffect(tx, e.id);
        removed++;
      } else if (!Beats.eq(start, e.start) || !Beats.eq(end, e.end)) {
        tx.update('effects', e.id, { start, end });
        moved++;
      }
    }
  }
  const unlinked = new Set<number>();
  const trackIds = scopeTracks(tx, lines, all);
  const forced = xyForcedCuts(tx, trackIds, from, to);
  const oldTempo = tx.tempo;
  const leadIns: MovedLeadIn[] = [];
  let splits: SplitOn | null = null;
  /** Curves spanning the whole range (no key inside) that were shortened as a whole (CR-W1-10b-1). */
  let shrunkCurves = 0;
  for (const trackId of trackIds) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const ctx = curveCtx(tx, info);
    const lead = captureLeadIn(keys, ctx, from);
    const counts = removeTimeKeys(keys, from, to, ctx, unlinked, forced.has(trackId));
    if (counts.shrunkCurve) shrunkCurves++;
    if (counts.removed > 0 || counts.moved > 0 || keys.some((k) => k.id === null)) {
      commitKeys(tx, info, keys);
      removed += counts.removed;
      moved += counts.moved;
      splits = collectSplit(splits, info, counts.report);
      // A shrink moves the first segment as a whole (a cut keeps the curve before `from` and reports its splits).
      if (lead !== null && counts.shrunk) leadIns.push({ info, before: lead, after: keys });
    }
  }
  cleanLinks(tx, unlinked);
  if (all) {
    const counts = removeTempoRange(tx, from, to);
    removed += counts.removed;
    moved += counts.moved;
    for (const m of [...tx.doc.markers.values()]) {
      if (inside(m.t)) {
        tx.remove('markers', m.id);
        removed++;
      } else if (Beats.lte(to, m.t)) {
        tx.update('markers', m.id, { t: back(m.t) });
        moved++;
      }
    }
    for (const v of [...tx.doc.videos.values()]) {
      const time = Beats.lte(to, v.time) ? back(v.time) : inside(v.time) ? from : v.time;
      if (!Beats.eq(time, v.time)) {
        tx.update('videos', v.id, { time });
        moved++;
      }
    }
    const meter: MeterMark[] = [];
    for (const m of tx.doc.timing.meter) {
      if (Beats.eq(m.t, Beats.ZERO)) meter.push(m);
      else if (inside(m.t)) continue;
      else meter.push(Beats.lte(to, m.t) ? { ...m, t: back(m.t) } : m);
    }
    const dedup = meter.filter((m, i) => meter.findIndex((x) => Beats.eq(x.t, m.t)) === i);
    if (!valueEquals(dedup, tx.doc.timing.meter)) tx.updateSingleton('timing', { meter: dedup });
  }
  noteWorstSplit(tx, worseSplitOn(splits, movedLeadIns(tx, leadIns, oldTempo)));
  if (shrunkCurves > 0) tx.note(reason(MOTION_MSG.timeShrunk, { count: shrunkCurves }));
  tx.setFocus({ beat: from });
  return { removed, moved };
};

/**
 * Position tracks of X/Y-linked lines that must be cut at [from, to) because their other axis (same layer) is cut
 * there and joins two sides, so both axes keep their keys at the same beats (link.ts).
 */
function xyForcedCuts(tx: TxBuilder, trackIds: readonly TrackId[], from: Beat, to: Beat): Set<TrackId> {
  const forced = new Set<TrackId>();
  const inScope = new Set(trackIds);
  for (const id of trackIds) {
    const info = requireTrack(tx, id);
    const partner = xyPartnerTrack(tx, info);
    if (partner === undefined || !inScope.has(partner)) continue;
    if (cutJoinsSides(readKeys(tx, id), from, to, curveCtx(tx, info))) forced.add(partner);
  }
  return forced;
}

/** Removes [from, to) from the BPM list; the tempo in effect at `to` continues at `from`. */
function removeTempoRange(tx: TxBuilder, from: Beat, to: Beat): { removed: number; moved: number } {
  const length = Beats.sub(to, from);
  let removed = 0;
  let moved = 0;
  const sorted = tx.idx
    .bpmSorted()
    .map((id) => tx.doc.bpm.get(id))
    .filter((b) => b !== undefined);
  let bpmAtTo = sorted[0]?.bpm ?? 120;
  for (const b of sorted) if (Beats.lte(b.t, to)) bpmAtTo = b.bpm;
  const zero = sorted.find((b) => Beats.eq(b.t, Beats.ZERO));
  for (const b of sorted) {
    if (Beats.eq(b.t, Beats.ZERO)) continue;
    if (Beats.lte(from, b.t) && Beats.lt(b.t, to)) {
      tx.remove('bpm', b.id);
      removed++;
    }
  }
  let landedAtFrom: BpmId | null = null;
  for (const b of sorted) {
    if (Beats.eq(b.t, Beats.ZERO) || Beats.lt(b.t, to)) continue;
    const t = Beats.sub(b.t, length, tx.rounding);
    if (Beats.eq(t, from)) landedAtFrom = b.id;
    if (Beats.eq(t, Beats.ZERO) && zero !== undefined) {
      // The point that was at `to` now starts the chart: its tempo moves onto the beat-0 point.
      tx.update('bpm', zero.id, { bpm: b.bpm });
      tx.remove('bpm', b.id);
      removed++;
      continue;
    }
    tx.update('bpm', b.id, { t });
    moved++;
  }
  if (landedAtFrom === null) {
    let inEffect = zero?.bpm ?? bpmAtTo;
    for (const b of sorted) if (Beats.lt(b.t, from)) inEffect = b.bpm;
    if (Beats.eq(from, Beats.ZERO) && zero !== undefined) {
      if (zero.bpm !== bpmAtTo) tx.update('bpm', zero.id, { bpm: bpmAtTo });
    } else if (inEffect !== bpmAtTo) {
      const id = tx.newId('bpm');
      tx.insert('bpm', { id, t: from, bpm: bpmAtTo });
    }
  }
  return { removed, moved };
}

// ---------------------------------------------------------------------------------------------------------------
// Reverse, negate, opacity
// ---------------------------------------------------------------------------------------------------------------

const reverseTime: TransformCommands['reverseTime'] = (tx, { notes, segments }) => {
  const noteRecs = [...new Set(notes)].map((id) => tx.doc.notes.get(id)).filter((n): n is Note => n !== undefined);
  const segIds = withXyPartners(
    tx,
    [...new Set(segments)].filter((id) => tx.doc.keys.has(id) && tx.idx.nextKey(id) !== undefined),
    true,
  );
  const span: { s: Beat | null; e: Beat | null } = { s: null, e: null };
  const widen = (a: Beat, b: Beat): void => {
    if (span.s === null || Beats.lt(a, span.s)) span.s = a;
    if (span.e === null || Beats.lt(span.e, b)) span.e = b;
  };
  for (const n of noteRecs) widen(n.t, n.end);
  for (const id of segIds) {
    const k = tx.doc.keys.get(id);
    const nextId = tx.idx.nextKey(id);
    const next = nextId === undefined ? undefined : tx.doc.keys.get(nextId);
    if (k !== undefined && next !== undefined) widen(k.t, next.t);
  }
  const start = span.s ?? tx.fail(reason(MOTION_MSG.reverseNothing));
  const end = span.e ?? start;
  const sum = Beats.add(start, end);
  for (const n of noteRecs) {
    const t = Beats.sub(sum, n.end);
    const e = Beats.sub(sum, n.t);
    const patch = notePatch(n, t, n.kind === 'hold' ? e : t);
    if (patch !== null) tx.update('notes', n.id, patch);
  }
  const unlinked = new Set<number>();
  let splits: SplitOn | null = null;
  let partnerSplit: SplitOn['res'] | null = null;
  if (Beats.lt(start, end)) {
    const tempoBeats = tempoChangeBeats(tx);
    const windows = new Map<string, XyWindow>();
    for (const trackId of groupByTrack(tx, segIds).keys()) {
      const info = requireTrack(tx, trackId);
      const keys = readKeys(tx, trackId);
      // The span's edges and tempo changes inside it are split shape-preservingly first (motionTime.ts).
      splits = collectSplit(splits, info, reverseWindow(keys, start, end, curveCtx(tx, info), unlinked, tempoBeats));
      commitKeys(tx, info, keys);
      noteXyWindow(tx, windows, info, start, end);
    }
    const aligned = alignXyWindows(tx, windows, unlinked);
    splits = worseSplitOn(splits, aligned.edited);
    partnerSplit = aligned.partner;
  }
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, splits);
  notePartnerSplit(tx, partnerSplit);
  tx.setFocus({ beat: start });
};

type ValueEdit = (keys: KeyRec[], i: number, info: TrackInfo) => boolean;

/** Applies `edit` to selected keys of line tracks; returns [applied, skipped]. */
function editKeys(tx: TxBuilder, ids: readonly KeyId[], edit: ValueEdit): [number, number] {
  let applied = 0;
  let skipped = 0;
  const touched: KeyId[] = [];
  for (const [trackId, tIds] of groupByTrack(tx, ids)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const sel = new Set(tIds);
    let any = false;
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i] as KeyRec;
      if (k.id === null || !sel.has(k.id)) continue;
      if (edit(keys, i, info)) {
        applied++;
        any = true;
        touched.push(k.id);
      } else skipped++;
    }
    if (any) commitKeys(tx, info, keys);
  }
  syncLinkGroups(
    tx,
    touched.flatMap((id) => linkedNeighbours(tx, id)),
  );
  let focus: Beat | null = null;
  for (const id of touched) {
    const k = tx.doc.keys.get(id);
    if (k !== undefined && (focus === null || Beats.lt(k.t, focus))) focus = k.t;
  }
  if (focus !== null) tx.setFocus({ beat: focus });
  return [applied, skipped];
}

function setTo(keys: KeyRec[], i: number, v: TrackValue): void {
  const k = keys[i] as KeyRec;
  if (i === 0) {
    k.vIn = v;
    k.vOut = v;
    return;
  }
  setBoth(keys, i, v);
  const next = keys[i + 1];
  if (k.ease.kind === 'hold' && next !== undefined) next.vIn = k.vOut;
}

const negateKeys: TransformCommands['negateKeys'] = (tx, { keys }) => {
  const [applied, skipped] = editKeys(tx, keys, (recs, i, info) => {
    const k = recs[i] as KeyRec;
    if (info.prop === 'alpha') {
      setTo(recs, i, 0);
      return true;
    }
    if (info.prop === null || !NEGATE_PROPS.has(info.prop)) return false;
    k.vIn = tidy(-(k.vIn as number));
    k.vOut = tidy(-(k.vOut as number));
    const next = recs[i + 1];
    if (k.ease.kind === 'hold' && next !== undefined && !sameValue(next.vIn, k.vOut)) next.vIn = k.vOut;
    return true;
  });
  if (applied === 0) tx.fail(reason(MOTION_MSG.negateNothing));
  if (skipped > 0) tx.note(reason(MOTION_MSG.negateSkipped, { count: skipped }));
};

const opacityFull: TransformCommands['opacityFull'] = (tx, { keys }) => {
  const [applied, skipped] = editKeys(tx, keys, (recs, i, info) => {
    if (info.prop !== 'alpha') return false;
    setTo(recs, i, 255);
    return true;
  });
  if (applied === 0) tx.fail(reason(MOTION_MSG.opacityNothing));
  if (skipped > 0) tx.note(reason(MOTION_MSG.opacitySkipped, { count: skipped }));
};

export const transformCommands: TransformCommands = {
  transform,
  insertTime,
  removeTime,
  reverseTime,
  negateKeys,
  opacityFull,
};
